import { lazy, Suspense, useState } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { AdminLayout } from "./app/layouts/AdminLayout";
import { QueryProvider } from "./app/providers/QueryProvider";
import { AuthGuard } from "./features/auth/AuthGuard";
import { AuthProvider } from "./features/auth/AuthProvider";
import { useAuth } from "./features/auth/auth-context";
import { DashboardPage } from "./features/dashboard/DashboardPage";
import { ApiError } from "./shared/api";
import { Alert, Button, Card, CardContent, LoadingState, PageHeader } from "./shared/components";

const CosmeticsPage = lazy(() => import("./features/profile-cosmetics/CosmeticsPage").then(module => ({ default: module.CosmeticsPage })));
const CosmeticEditorPage = lazy(() => import("./features/profile-cosmetics/CosmeticEditorPage").then(module => ({ default: module.CosmeticEditorPage })));
const CosmeticOffersPage = lazy(() => import("./features/profile-cosmetics/CosmeticOffersPage").then(module => ({ default: module.CosmeticOffersPage })));
const LoginPage = lazy(() =>
  import("./features/auth/LoginPage").then((module) => ({ default: module.LoginPage }))
);
const PlayersPage = lazy(() =>
  import("./features/players/PlayersPage").then((module) => ({ default: module.PlayersPage }))
);
const PlayerCreatePage = lazy(() =>
  import("./features/players/PlayerCreatePage").then((module) => ({ default: module.PlayerCreatePage }))
);
const BugReportsPage = lazy(() => import("./features/bug-reports/BugReportsPage").then((module) => ({ default: module.BugReportsPage })));
const BugReportDetailPage = lazy(() => import("./features/bug-reports/BugReportDetailPage").then((module) => ({ default: module.BugReportDetailPage })));

const PlayerReportsPage = lazy(() =>
  import("./features/player-reports/PlayerReportsPage").then((module) => ({ default: module.PlayerReportsPage }))
);
const PlayerReportTargetPage = lazy(() =>
  import("./features/player-reports/PlayerReportTargetPage").then((module) => ({ default: module.PlayerReportTargetPage }))
);
const UserDetailPage = lazy(() =>
  import("./features/players/UserDetailPage").then((module) => ({ default: module.UserDetailPage }))
);
const ProfileDetailPage = lazy(() =>
  import("./features/players/ProfileDetailPage").then((module) => ({ default: module.ProfileDetailPage }))
);
const AvatarsPage = lazy(() =>
  import("./features/avatars/AvatarsPage").then((module) => ({ default: module.AvatarsPage }))
);
const CreateAvatarPage = lazy(() =>
  import("./features/avatars/CreateAvatarPage").then((module) => ({ default: module.CreateAvatarPage }))
);
const AvatarDetailPage = lazy(() =>
  import("./features/avatars/AvatarDetailPage").then((module) => ({ default: module.AvatarDetailPage }))
);
const EditAvatarPage = lazy(() =>
  import("./features/avatars/EditAvatarPage").then((module) => ({ default: module.EditAvatarPage }))
);
const ClansPage = lazy(() =>
  import("./features/clans/ClansPage").then((module) => ({ default: module.ClansPage }))
);
const ClanCreatePage = lazy(() =>
  import("./features/clans/ClanCreatePage").then((module) => ({ default: module.ClanCreatePage }))
);
const ClanDetailPage = lazy(() =>
  import("./features/clans/ClanDetailPage").then((module) => ({ default: module.ClanDetailPage }))
);
const AuditLogPage = lazy(() =>
  import("./features/governance/AuditLogPage").then((module) => ({ default: module.AuditLogPage }))
);
const AuditDetailPage = lazy(() =>
  import("./features/governance/AuditDetailPage").then((module) => ({ default: module.AuditDetailPage }))
);
const RolesPage = lazy(() =>
  import("./features/governance/RolesPage").then((module) => ({ default: module.RolesPage }))
);
const RoleDetailPage = lazy(() =>
  import("./features/governance/RoleDetailPage").then((module) => ({ default: module.RoleDetailPage }))
);
const AnnouncementsPage = lazy(() =>
  import("./features/announcements/AnnouncementsPage").then((module) => ({
    default: module.AnnouncementsPage
  }))
);
const RuntimeMaintenancePage = lazy(() =>
  import("./features/runtime-maintenance/RuntimeMaintenancePage").then((module) => ({
    default: module.RuntimeMaintenancePage
  }))
);
const WaitlistPage = lazy(() =>
  import("./features/waitlist/WaitlistPage").then((module) => ({
    default: module.WaitlistPage
  }))
);

export default function App() {
  return (
    <BrowserRouter>
      <QueryProvider>
        <AuthProvider>
          <Suspense fallback={<RouteLoading />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route element={<AuthGuard />}>
                <Route element={<AuthenticatedLayout />}>
                  <Route index element={<DashboardPage />} />
                  <Route path="jogadores" element={<PlayersPage />} />
                  <Route path="jogadores/novo" element={<PlayerCreatePage />} />
                  <Route path="bugs" element={<BugReportsPage />} />
                  <Route path="bugs/:reportId" element={<BugReportDetailPage />} />
                  <Route path="denuncias" element={<PlayerReportsPage />} />
                  <Route path="denuncias/:profileId" element={<PlayerReportTargetPage />} />
                  <Route path="jogadores/:userId" element={<UserDetailPage />} />
                  <Route path="perfis/:profileId" element={<ProfileDetailPage />} />
                  <Route path="ferramentas/economia" element={<Navigate to="/jogadores" replace />} />
                  <Route path="ferramentas/inventario" element={<Navigate to="/jogadores" replace />} />
                  <Route path="ferramentas/sessoes" element={<Navigate to="/jogadores" replace />} />
                  <Route path="cosmeticos" element={<CosmeticsPage />} />
                  <Route path="cosmeticos/novo" element={<CosmeticEditorPage />} />
                  <Route path="cosmeticos/ofertas" element={<CosmeticOffersPage />} />
                  <Route path="cosmeticos/:itemId" element={<CosmeticEditorPage />} />
                  <Route path="avatares" element={<AvatarsPage />} />
                  <Route path="avatares/novo" element={<CreateAvatarPage />} />
                  <Route path="avatares/:avatarItemId" element={<AvatarDetailPage />} />
                  <Route path="avatares/:avatarItemId/editar" element={<EditAvatarPage />} />
                  <Route path="clans" element={<ClansPage />} />
                  <Route path="clans/novo" element={<ClanCreatePage />} />
                  <Route path="clans/:clanId" element={<ClanDetailPage />} />
                  <Route path="comunicacao/anuncios" element={<AnnouncementsPage />} />
                  <Route path="operacoes/manutencao" element={<RuntimeMaintenancePage />} />
                  <Route path="waitlist" element={<WaitlistPage />} />
                  <Route path="auditoria" element={<AuditLogPage />} />
                  <Route path="auditoria/:auditId" element={<AuditDetailPage />} />
                  <Route path="acessos/roles" element={<RolesPage />} />
                  <Route path="acessos/roles/:roleId" element={<RoleDetailPage />} />
                  <Route path="nao-encontrado" element={<NotFoundPage />} />
                  <Route path="*" element={<Navigate to="/nao-encontrado" replace />} />
                </Route>
              </Route>
            </Routes>
          </Suspense>
        </AuthProvider>
      </QueryProvider>
    </BrowserRouter>
  );
}

function AuthenticatedLayout() {
  const auth = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);

  async function handleLogout() {
    setIsLoggingOut(true);
    setLogoutError(null);
    try {
      await auth.logout();
    } catch (error) {
      setLogoutError(
        error instanceof ApiError
          ? error.message
          : "Não foi possível confirmar o encerramento da sessão. Tente novamente."
      );
    } finally {
      setIsLoggingOut(false);
    }
  }

  return (
    <AdminLayout
      operator={
        auth.operator
          ? {
              email: auth.operator.email,
              displayName: auth.operator.displayName,
              roleLabel: "Sessão administrativa"
            }
          : undefined
      }
      onLogout={() => void handleLogout()}
      isLoggingOut={isLoggingOut}
    >
      {logoutError ? (
        <Alert variant="danger" title="Não foi possível sair" className="mb-6">
          {logoutError} Tente novamente.
        </Alert>
      ) : null}
      <Outlet />
    </AdminLayout>
  );
}

function RouteLoading() {
  return (
    <main className="grid min-h-dvh place-items-center bg-background px-5">
      <Card className="w-full max-w-xl">
        <LoadingState label="Carregando módulo administrativo" />
      </Card>
    </main>
  );
}

function NotFoundPage() {
  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="404"
        title="Página não encontrada"
        description="O endereço informado não corresponde a uma área administrativa conhecida."
      />
      <Card className="max-w-2xl">
        <CardContent>
          <Button onClick={() => window.location.assign("/")}>Voltar à visão geral</Button>
        </CardContent>
      </Card>
    </div>
  );
}
